import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { avatarService } from '../../../services/avatarService';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const AccountSummary = ({ stats, userProfile }) => {
  const { user } = useAuth();
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Load avatar URL on mount and when userProfile changes
  useEffect(() => {
    const loadAvatar = async () => {
      if (userProfile?.avatar_url) {
        const url = await avatarService?.getAvatarUrl(userProfile?.avatar_url);
        setAvatarUrl(url);
      }
    };
    loadAvatar();
  }, [userProfile?.avatar_url]);

  // Handle avatar upload
  const handleAvatarUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file || !user?.id) return;

    setUploading(true);
    setUploadError(null);

    try {
      // Delete old avatar if exists
      if (userProfile?.avatar_url) {
        await avatarService?.deleteAvatar(userProfile?.avatar_url);
      }

      // Upload new avatar
      await avatarService?.uploadAvatar(user?.id, file);

      // Reload page to refresh auth context with new avatar
      window.location?.reload();
    } catch (error) {
      console.error('Avatar upload failed:', error);
      setUploadError(error?.message || 'Failed to upload avatar. Please try again.');
      setUploading(false);
    }
  };

  // Format date to show month and year only
  const formatMemberSince = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  };

  // Extract first name from full name
  const getFirstName = (fullName) => {
    if (!fullName) return 'User';
    return fullName?.split(' ')?.[0];
  };

  return (
    <div className="bg-card border border-border rounded-organic shadow-organic p-6">
      <div className="text-center mb-6">
        <div className="relative inline-block mb-4">
          {/* Avatar with upload overlay */}
          <div className="relative group">
            <div className="w-20 h-20 rounded-full overflow-hidden bg-muted flex items-center justify-center">
              {avatarUrl ? (
                <Image
                  src={avatarUrl}
                  alt={`${getFirstName(userProfile?.full_name)}'s avatar`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Icon name="User" size={32} className="text-muted-foreground" />
              )}
            </div>
            
            {/* Upload overlay on hover */}
            <label 
              htmlFor="avatar-upload"
              className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
            >
              <Icon name={uploading ? "Loader2" : "Camera"} size={20} className={`text-white ${uploading ? 'animate-spin' : ''}`} />
            </label>
            
            {/* Hidden file input */}
            <input
              id="avatar-upload"
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleAvatarUpload}
              disabled={uploading}
              className="hidden"
            />
          </div>
          
          <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-success rounded-full border-2 border-background flex items-center justify-center">
            <Icon name="CheckCircle" size={14} className="text-white" />
          </div>
        </div>
        
        {uploadError && (
          <p className="text-xs text-error mb-2">{uploadError}</p>
        )}
        
        <h3 className="font-heading font-heading-bold text-lg text-card-foreground mb-1">
          {getFirstName(userProfile?.full_name)}
        </h3>
        <p className="font-body text-sm text-muted-foreground mb-4">
          {userProfile?.email || user?.email}
        </p>
        
        <Link to="/user-profile-management">
          <Button variant="outline" size="sm" iconName="Settings">
            Edit Profile
          </Button>
        </Link>
      </div>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-3 bg-background rounded-organic">
            <div className="font-heading font-heading-bold text-2xl text-foreground">
              {stats?.totalTrips || 0}
            </div>
            <div className="font-body text-xs text-muted-foreground">
              Total Trips
            </div>
          </div>
          <div className="text-center p-3 bg-background rounded-organic">
            <div className="font-heading font-heading-bold text-2xl text-primary">
              {stats?.upcomingTrips || 0}
            </div>
            <div className="font-body text-xs text-muted-foreground">
              Upcoming
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-3 bg-background rounded-organic">
            <div className="font-heading font-heading-bold text-2xl text-success">
              {stats?.completedTrips || 0}
            </div>
            <div className="font-body text-xs text-muted-foreground">
              Completed
            </div>
          </div>
          <div className="text-center p-3 bg-background rounded-organic">
            <div className="font-heading font-heading-bold text-2xl text-blue-600">
              {stats?.planningTrips || 0}
            </div>
            <div className="font-body text-xs text-muted-foreground">
              Planning
            </div>
          </div>
        </div>

        {stats?.totalCost > 0 && (
          <div className="text-center p-3 bg-accent/10 border border-accent/20 rounded-organic">
            <div className="font-heading font-heading-bold text-xl text-accent">
              ${stats?.totalCost?.toLocaleString()}
            </div>
            <div className="font-body text-xs text-muted-foreground">
              Total Trip Value
            </div>
          </div>
        )}

        {stats?.nextTrip && (
          <Link 
            to={`/trip-detail-view/${stats?.nextTrip?.id}`}
            state={{ 
              tripId: stats?.nextTrip?.id,
              tripData: stats?.nextTrip 
            }}
            className="block p-4 bg-primary/10 border border-primary/20 rounded-organic hover:bg-primary/20 transition-colors"
          >
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Calendar" size={16} className="text-primary" />
              <span className="font-body-medium text-primary text-sm">Next Trip</span>
            </div>
            <h4 className="font-heading font-heading-bold text-base text-foreground mb-1">
              {stats?.nextTrip?.destination}
            </h4>
            {stats?.nextTrip?.park && (
              <p className="font-body text-xs text-muted-foreground mb-2">
                {stats?.nextTrip?.park}
              </p>
            )}
            <div className="flex items-center justify-between">
              <div className="font-body text-xs text-muted-foreground">
                {stats?.nextTrip?.start_date && stats?.nextTrip?.end_date ? (
                  <>
                    {new Date(stats?.nextTrip?.start_date)?.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    })}
                    {' - '}
                    {new Date(stats?.nextTrip?.end_date)?.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </>
                ) : stats?.nextTrip?.start_date ? (
                  new Date(stats?.nextTrip?.start_date)?.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })
                ) : (
                  'Date TBA'
                )}
              </div>
              <Icon name="ArrowRight" size={14} className="text-primary" />
            </div>
          </Link>
        )}
      </div>
      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-center justify-between text-sm">
          <span className="font-body text-muted-foreground">Member since</span>
          <span className="font-body-medium text-foreground">
            {formatMemberSince(userProfile?.created_at)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default AccountSummary;